/**
 * Helper functions for working with translations in recipes and ingredients
 */

export interface TranslationMap {
  [languageCode: string]: string;
}

export interface TranslatableEntity {
  name?: string;
  description?: string;
  name_translations?: TranslationMap;
  description_translations?: TranslationMap;
}

export interface TranslatableRecipe extends TranslatableEntity {
  recipeIngredient?: TranslatableRecipeIngredient[];
}

export interface TranslatableRecipeIngredient extends TranslatableEntity {
  ingredient?: TranslatableEntity;
}

/**
 * Get translated text with fallback to original
 * @param originalText - The original text
 * @param translations - Translation map object
 * @param languageCode - Target language code (e.g., 'zh-HK', 'zh-CN')
 * @returns Translated text or original text if translation not found
 */
export function getTranslatedText(
  originalText: string,
  translations: TranslationMap | undefined,
  languageCode: string
): string {
  if (!translations || !languageCode) {
    return originalText;
  }
  
  return translations[languageCode] || originalText;
}

/**
 * Get translated recipe name
 * @param recipe - Recipe object with potential translations
 * @param languageCode - Target language code
 * @returns Translated name or original name
 */
export function getTranslatedRecipeName(
  recipe: TranslatableRecipe,
  languageCode: string
): string {
  return getTranslatedText(recipe.name || '', recipe.name_translations, languageCode);
}

/**
 * Get translated recipe description
 * @param recipe - Recipe object with potential translations
 * @param languageCode - Target language code
 * @returns Translated description or original description
 */
export function getTranslatedRecipeDescription(
  recipe: TranslatableRecipe,
  languageCode: string
): string {
  return getTranslatedText(recipe.description || '', recipe.description_translations, languageCode);
}

/**
 * Get translated ingredient name
 * @param ingredient - Ingredient object with potential translations
 * @param languageCode - Target language code
 * @returns Translated name or original name
 */
export function getTranslatedIngredientName(
  ingredient: TranslatableEntity,
  languageCode: string
): string {
  return getTranslatedText(ingredient.name || '', ingredient.name_translations, languageCode);
}

/**
 * Transform a recipe to use translated text as primary text
 * @param recipe - Original recipe object
 * @param languageCode - Target language code
 * @returns Recipe with translated text as primary fields
 */
export function getLocalizedRecipe(
  recipe: TranslatableRecipe,
  languageCode: string
): TranslatableRecipe {
  const localizedRecipe = { ...recipe };
  
  // Translate recipe name and description
  localizedRecipe.name = getTranslatedRecipeName(recipe, languageCode);
  localizedRecipe.description = getTranslatedRecipeDescription(recipe, languageCode);
  
  // Translate ingredient names
  if (recipe.recipeIngredient && Array.isArray(recipe.recipeIngredient)) {
    localizedRecipe.recipeIngredient = recipe.recipeIngredient.map(ri => ({
      ...ri,
      name: getTranslatedIngredientName(ri, languageCode),
      ingredient: ri.ingredient ? {
        ...ri.ingredient,
        name: getTranslatedIngredientName(ri.ingredient, languageCode)
      } : ri.ingredient
    }));
  }
  
  return localizedRecipe;
}

/**
 * Check if a recipe has translations for a specific language
 * @param recipe - Recipe object to check
 * @param languageCode - Target language code
 * @returns True if recipe has any translations for the language
 */
export function hasTranslations(
  recipe: TranslatableRecipe,
  languageCode: string
): boolean {
  // Check recipe name translation
  if (recipe.name_translations?.[languageCode]) {
    return true;
  }
  
  // Check recipe description translation
  if (recipe.description_translations?.[languageCode]) {
    return true;
  }
  
  // Check ingredient translations
  if (recipe.recipeIngredient && Array.isArray(recipe.recipeIngredient)) {
    for (const ri of recipe.recipeIngredient) {
      if (ri.name_translations?.[languageCode]) {
        return true;
      }
      if (ri.ingredient?.name_translations?.[languageCode]) {
        return true;
      }
    }
  }
  
  return false;
}

/**
 * Get translation completion percentage for a recipe
 * @param recipe - Recipe object to analyze
 * @param languageCode - Target language code
 * @returns Percentage (0-100) of fields that have translations
 */
export function getTranslationCompleteness(
  recipe: TranslatableRecipe,
  languageCode: string
): number {
  let totalFields = 0;
  let translatedFields = 0;
  
  // Count recipe name
  if (recipe.name) {
    totalFields++;
    if (recipe.name_translations?.[languageCode]) {
      translatedFields++;
    }
  }
  
  // Count recipe description
  if (recipe.description) {
    totalFields++;
    if (recipe.description_translations?.[languageCode]) {
      translatedFields++;
    }
  }
  
  // Count ingredient names
  if (recipe.recipeIngredient && Array.isArray(recipe.recipeIngredient)) {
    recipe.recipeIngredient.forEach(ri => {
      if (ri.name) {
        totalFields++;
        if (ri.name_translations?.[languageCode]) {
          translatedFields++;
        }
      }
      
      if (ri.ingredient?.name) {
        totalFields++;
        if (ri.ingredient.name_translations?.[languageCode]) {
          translatedFields++;
        }
      }
    });
  }
  
  return totalFields > 0 ? Math.round((translatedFields / totalFields) * 100) : 0;
}

/**
 * Get available languages for a recipe (languages that have at least one translation)
 * @param recipe - Recipe object to analyze
 * @returns Array of language codes that have translations
 */
export function getAvailableLanguages(recipe: TranslatableRecipe): string[] {
  const languages = new Set<string>();
  
  // Check recipe translations
  if (recipe.name_translations) {
    Object.keys(recipe.name_translations).forEach(lang => languages.add(lang));
  }
  if (recipe.description_translations) {
    Object.keys(recipe.description_translations).forEach(lang => languages.add(lang));
  }
  
  // Check ingredient translations
  if (recipe.recipeIngredient && Array.isArray(recipe.recipeIngredient)) {
    recipe.recipeIngredient.forEach(ri => {
      if (ri.name_translations) {
        Object.keys(ri.name_translations).forEach(lang => languages.add(lang));
      }
      if (ri.ingredient?.name_translations) {
        Object.keys(ri.ingredient.name_translations).forEach(lang => languages.add(lang));
      }
    });
  }
  
  return Array.from(languages);
}
